import React, { useEffect, useMemo } from 'react'
import SuggestNewCarrierFeedback from './SuggestNewCarrierFeedback'
import PackageTimelineCarrierInformation from './CarrierOrderTimeline'
import { deepCompare } from '../../../libraries/utils'
import { IOrderResponse } from '../../../libraries/interfaces/tracker.interfaces'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import PackageModel from '@geartrack/shared-lib/dist/PackageModel'
import { ICarrierListSupportedResponse } from '@geartrack/shared-lib/dist/tracker-api/httpResponsesTypes'
import useFetchTrackingInfo from '../../../hooks/useFetchTrackingInfo'

type Props = {
  pkg: PackageModel
  setLoading: React.Dispatch<boolean>
  carriersListResponse: ICarrierListSupportedResponse
}

const PackageTimeline = ({ pkg, carriersListResponse, setLoading }: Props) => {
  const { t, i18n } = useTranslation('packages')
  const { responses, isLoading } = useFetchTrackingInfo(pkg, i18n.language, carriersListResponse)

  const { sucessfulResponses, errorResponses } = useMemo(() => {
    return {
      sucessfulResponses: responses.filter(r => r && r.order),
      errorResponses: responses.filter(r => r === null),
    }
  }, [responses])

  // bubble up end of loading state (all responses fetched)
  useEffect(() => {
    if (isLoading === false) {
      setLoading(false)
    }
  }, [isLoading, setLoading])

  const sortedResponses = useMemo(() => {
    return sortByDescState(sucessfulResponses)
  }, [sucessfulResponses])

  if (sucessfulResponses.length === 0 && isLoading) {
    return (
      <div>
        <i className="fas fa-spin fa-circle-notch" /> {t('timeline.fetching from carriers')}
      </div>
    )
  }

  if (errorResponses.length === carriersListResponse.carriers.length) {
    return <div>{t('timeline.all carriers failed')}</div>
  }

  if (sucessfulResponses.length === 0 && !isLoading) {
    const carriers = carriersListResponse.carriers.map(c => c.fullName).join(', ')
    return <SuggestNewCarrierFeedback message={t('timeline.tried on the following carriers', { carriers })} pkg={pkg} />
  }

  return (
    <div>
      {sortedResponses.map(res => (
        <PackageTimelineCarrierInformation key={res.id.computedKey} orderResponse={res} />
      ))}
    </div>
  )
}

function sortByDescState(orderResponses: IOrderResponse[]) {
  return [...orderResponses].sort((a, b) => {
    let dateA = moment(b.order.states[0].date)
    let dateB = moment(a.order.states[0].date)

    if (dateA > dateB) return 1
    else if (dateA < dateB) return -1
    else return 0
  })
}

export default React.memo(PackageTimeline, deepCompare)
